import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useCompany } from '../hooks/useCompany'
import { Button, Input, Label } from '../components/ui'
import { Users, Plus, ChevronRight, ChevronDown, Shield, Building, Briefcase, FileText } from 'lucide-react'
import { AddRuleModal } from '../components/teams/AddRuleModal'

interface Team {
  id: string
  name: string
  company_id: string
  parent_team_id?: string
  owner_user_id: string
  tier: number
  depth: number
  path: string
  root_team_id: string
  member_count: number
  child_team_count: number
  created_at: number
  user_role: 'TEAM_ADMIN' | 'MEMBER' | null
  children?: Team[]
}

interface TeamRule {
  id: string
  rule_type: string
  action: 'BLOCK' | 'WARN' | 'REQUIRE_APPROVAL'
  config: any
  is_inherited: boolean
  priority: number
  is_overridden: boolean
}

export default function TeamsWithHierarchy() {
  const { user } = useAuth()
  const { currentCompany, loading: companyLoading } = useCompany()
  const [teams, setTeams] = useState<Team[]>([])
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set())
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [teamRules, setTeamRules] = useState<TeamRule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showCreateTeam, setShowCreateTeam] = useState(false)
  const [showCreateSubteam, setShowCreateSubteam] = useState(false)
  const [showAddRule, setShowAddRule] = useState(false)
  const [parentTeamForSubteam, setParentTeamForSubteam] = useState<Team | null>(null)
  const [newTeamForm, setNewTeamForm] = useState({
    name: '',
    parent_team_id: '',
    tier: 1
  })
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (currentCompany && !companyLoading) {
      fetchTeams()
    } else {
      setLoading(false)
      setTeams([])
    }
  }, [currentCompany, companyLoading])

  const fetchTeams = async () => {
    if (!currentCompany) return

    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/teams?company_id=${currentCompany.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to load teams')
      }

      const data = await response.json()
      
      // Build hierarchical structure
      const teamMap = new Map<string, Team>()
      const rootTeams: Team[] = []
      
      // First pass: create all teams
      data.teams.forEach((team: Team) => {
        teamMap.set(team.id, { ...team, children: [] })
      })
      
      // Second pass: build hierarchy
      data.teams.forEach((team: Team) => {
        const currentTeam = teamMap.get(team.id)!
        if (team.parent_team_id) {
          const parent = teamMap.get(team.parent_team_id)
          if (parent) {
            parent.children!.push(currentTeam)
          } else {
            rootTeams.push(currentTeam)
          }
        } else {
          rootTeams.push(currentTeam)
        }
      })
      
      setTeams(rootTeams)
    } catch (err) {
      console.error('Failed to fetch teams:', err)
      setError(err instanceof Error ? err.message : 'Failed to load teams')
    } finally {
      setLoading(false)
    }
  }

  const fetchTeamRules = async (teamId: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/teams/${teamId}/rules`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        setTeamRules(data.rules)
      }
    } catch (err) {
      console.error('Failed to fetch team rules:', err)
    }
  }

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentCompany) return

    try {
      setCreating(true)
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/teams`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: newTeamForm.name,
            company_id: currentCompany.id,
            parent_team_id: newTeamForm.parent_team_id || undefined,
            tier: newTeamForm.tier
          })
        }
      )

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to create team')
      }

      // Reset form and refresh
      setNewTeamForm({ name: '', parent_team_id: '', tier: 1 })
      setShowCreateTeam(false)
      setShowCreateSubteam(false)
      setParentTeamForSubteam(null)
      await fetchTeams()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team')
    } finally {
      setCreating(false)
    }
  }

  const toggleTeamExpansion = (teamId: string) => {
    const newExpanded = new Set(expandedTeams)
    if (newExpanded.has(teamId)) {
      newExpanded.delete(teamId)
    } else {
      newExpanded.add(teamId)
    }
    setExpandedTeams(newExpanded)
  }

  const handleSelectTeam = (team: Team) => {
    setSelectedTeam(team)
    fetchTeamRules(team.id)
  }

  const openCreateSubteam = (parentTeam: Team) => {
    setParentTeamForSubteam(parentTeam)
    setNewTeamForm({ 
      name: '', 
      parent_team_id: parentTeam.id,
      tier: parentTeam.tier 
    })
    setShowCreateSubteam(true)
  }

  const handleCreateRule = async (ruleData: any) => {
    if (!selectedTeam) return

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/teams/${selectedTeam.id}/rules`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(ruleData)
        }
      )

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to create rule')
      }

      setSuccess('Rule created successfully')
      setTimeout(() => setSuccess(null), 3000)
      
      // Refresh team rules
      await fetchTeamRules(selectedTeam.id)
    } catch (err) {
      throw err // Re-throw to be handled by the modal
    }
  }

  const getRuleTypeLabel = (ruleType: string): string => {
    const labels: Record<string, string> = {
      'REQUIRE_LOCATION_CLOCK_IN': 'Require Location for Clock In',
      'REQUIRE_PHOTO_CLOCK_IN': 'Require Photo for Clock In',
      'REQUIRE_QR_CLOCK_IN': 'Require QR Code for Clock In',
      'RESTRICT_CLOCK_IN_GEOFENCE': 'Restrict Clock In to Geofence',
      'RESTRICT_CLOCK_IN_TIME_WINDOW': 'Restrict Clock In Time Window',
      'REQUIRE_MANAGER_APPROVAL': 'Require Manager Approval',
      'MAX_SHIFT_DURATION': 'Maximum Shift Duration',
      'MIN_BREAK_DURATION': 'Minimum Break Duration',
      'OVERTIME_THRESHOLD': 'Overtime Threshold'
    }
    return labels[ruleType] || ruleType
  }

  const renderTeamTree = (teams: Team[], level: number = 0) => {
    return teams.map(team => (
      <div key={team.id} className={`${level > 0 ? 'ml-8' : ''}`}>
        <div
          className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
            selectedTeam?.id === team.id 
              ? 'bg-blue-50 border border-blue-200' 
              : 'hover:bg-gray-50'
          }`}
          onClick={() => handleSelectTeam(team)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (team.children && team.children.length > 0) {
                toggleTeamExpansion(team.id)
              }
            }}
            className="mr-2"
          >
            {team.children && team.children.length > 0 ? (
              expandedTeams.has(team.id) ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )
            ) : (
              <div className="w-4" />
            )}
          </button>
          
          <Building className="w-5 h-5 text-gray-400 mr-3" />
          
          <div className="flex-1">
            <div className="flex items-center">
              <h3 className="font-medium text-gray-900">{team.name}</h3>
              {team.user_role === 'TEAM_ADMIN' && (
                <Shield className="w-4 h-4 text-blue-600 ml-2" />
              )}
            </div>
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <Users className="w-4 h-4 mr-1" />
              <span>{team.member_count} members</span>
              {team.child_team_count > 0 && (
                <>
                  <span className="mx-2">•</span>
                  <span>{team.child_team_count} sub-teams</span>
                </>
              )}
            </div>
          </div>
          
          {team.user_role === 'TEAM_ADMIN' && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                openCreateSubteam(team)
              }}
              className="ml-2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title="Add sub-team"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {expandedTeams.has(team.id) && team.children && team.children.length > 0 && (
          <div className="mt-1">
            {renderTeamTree(team.children, level + 1)}
          </div>
        )}
      </div>
    ))
  }

  if (loading || companyLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading teams...</p>
        </div>
      </div>
    )
  }

  if (!currentCompany) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Company Selected</h3>
          <p className="text-gray-600">Please select a company to view teams.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full">
      {/* Teams Hierarchy Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Teams</h2>
            {['OWNER', 'ADMIN'].includes(user?.role || '') && (
              <Button
                onClick={() => setShowCreateTeam(true)}
                size="sm"
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Team
              </Button>
            )}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {teams.length === 0 ? (
            <div className="text-center py-8">
              <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No teams yet</p>
            </div>
          ) : (
            renderTeamTree(teams)
          )}
        </div>
      </div>

      {/* Team Details */}
      <div className="flex-1 bg-gray-50">
        {selectedTeam ? (
          <div className="p-6">
            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800">{success}</p>
              </div>
            )}
            
            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800">{error}</p>
              </div>
            )}
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{selectedTeam.name}</h1>
                  {selectedTeam.parent_team_id && (
                    <p className="text-sm text-gray-500 mt-1">
                      Sub-team of {teams.find(t => t.id === selectedTeam.parent_team_id)?.name}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedTeam.tier === 3 ? 'bg-purple-100 text-purple-800' :
                    selectedTeam.tier === 2 ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    Tier {selectedTeam.tier}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Members</p>
                  <p className="text-2xl font-semibold text-gray-900">{selectedTeam.member_count}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sub-teams</p>
                  <p className="text-2xl font-semibold text-gray-900">{selectedTeam.child_team_count}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Your Role</p>
                  <p className="text-lg font-medium text-gray-900">
                    {selectedTeam.user_role === 'TEAM_ADMIN' ? 'Admin' : 
                     selectedTeam.user_role === 'MEMBER' ? 'Member' : 'Not a member'}
                  </p>
                </div>
              </div>
            </div>

            {/* Team Rules Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Team Rules & Requirements</h2>
                {selectedTeam.user_role === 'TEAM_ADMIN' && (
                  <Button
                    onClick={() => setShowAddRule(true)}
                    size="sm"
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Rule
                  </Button>
                )}
              </div>
              
              {teamRules.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No rules configured for this team</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {teamRules.map((rule) => (
                    <div key={rule.id} className={`p-4 rounded-lg border ${
                      rule.is_inherited ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h3 className="font-medium text-gray-900">
                              {getRuleTypeLabel(rule.rule_type)}
                            </h3>
                            {rule.is_inherited && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                                Inherited
                              </span>
                            )}
                            {rule.is_overridden && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-200 text-yellow-800 rounded">
                                Overridden
                              </span>
                            )}
                          </div>
                          <div className="flex items-center mt-2 space-x-4">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              rule.action === 'BLOCK' ? 'bg-red-100 text-red-800' :
                              rule.action === 'WARN' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {rule.action}
                            </span>
                            <span className="text-sm text-gray-500">
                              Priority: {rule.priority}
                            </span>
                          </div>
                          {rule.config.message && (
                            <p className="text-sm text-gray-600 mt-2">{rule.config.message}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Team</h3>
              <p className="text-gray-600">Choose a team from the list to view details and manage settings.</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Team Modal */}
      {(showCreateTeam || showCreateSubteam) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {showCreateSubteam ? `Create Sub-team for ${parentTeamForSubteam?.name}` : 'Create New Team'}
              </h2>
              <form onSubmit={handleCreateTeam}>
                <div className="mb-4">
                  <Label htmlFor="team-name">Team Name</Label>
                  <Input
                    id="team-name"
                    type="text"
                    value={newTeamForm.name}
                    onChange={(e) => setNewTeamForm({ ...newTeamForm, name: e.target.value })}
                    placeholder="Enter team name"
                    required
                  />
                </div>
                
                {!showCreateSubteam && (
                  <>
                    <div className="mb-4">
                      <Label htmlFor="parent-team">Parent Team (Optional)</Label>
                      <select
                        id="parent-team"
                        value={newTeamForm.parent_team_id}
                        onChange={(e) => setNewTeamForm({ ...newTeamForm, parent_team_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">None (Root Team)</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.id}>{team.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="mb-4">
                      <Label htmlFor="team-tier">Team Tier</Label>
                      <select
                        id="team-tier"
                        value={newTeamForm.tier}
                        onChange={(e) => setNewTeamForm({ ...newTeamForm, tier: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value={1}>Tier 1 - Basic</option>
                        <option value={2}>Tier 2 - Professional</option>
                        <option value={3}>Tier 3 - Enterprise</option>
                      </select>
                    </div>
                  </>
                )}

                <div className="flex justify-end space-x-3">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowCreateTeam(false)
                      setShowCreateSubteam(false)
                      setParentTeamForSubteam(null)
                      setNewTeamForm({ name: '', parent_team_id: '', tier: 1 })
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={creating}
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {creating ? 'Creating...' : 'Create Team'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Rule Modal */}
      {showAddRule && selectedTeam && (
        <AddRuleModal
          isOpen={showAddRule}
          onClose={() => setShowAddRule(false)}
          onSave={handleCreateRule}
          teamId={selectedTeam.id}
          teamName={selectedTeam.name}
        />
      )}
    </div>
  )
}